<template>
  <app-page
    :title="$t('employee.add.pageTitle')"
    :subtitle="$t('employee.add.pageSubtitle')"
    :error="error"
    icon="user-plus"
  >
    <template v-slot:infobox>
      <sme-info-box :title="$t('employee.add.afterInvitationTitle')" id="after-invitation" save-state>
        <p>{{ $t('employee.add.afterInvitationBody1') }}</p>
        <p>
          <i18n path="employee.add.afterInvitationBody2" tag="span">
            <template #enroll
              ><strong>{{ $t('employee.add.enrollBold') }}</strong></template
            >
          </i18n>
        </p>
        <p>
          {{ $t('employee.add.afterInvitationBody3') }}
        </p>
      </sme-info-box>

      <sme-info-box :title="$t('employee.add.shiftAndSalariedTitle')" id="shift-and-salaried" save-state>
        <p>{{ $t('employee.add.yesBody') }}</p>
        <p>
          <i18n path="employee.add.salariedBody" tag="span">
            <template #startDate
              ><strong>{{ $t('employee.add.startDateBold') }}</strong></template
            >
          </i18n>
        </p>
        <p>
          <i18n path="employee.add.shiftBody" tag="span">
            <template #shifts
              ><router-link :to="{ name: 'shifts' }">{{ $t('employee.enroll.shiftsLink') }}</router-link></template
            >
            <template #rotaIntegrations
              ><router-link :to="{ name: 'sme-integrations' }">{{
                $t('employee.add.rotaIntegrationsLink')
              }}</router-link></template
            >
          </i18n>
        </p>
      </sme-info-box>

      <sme-info-box :title="$t('employee.enroll.codeTitle')" id="employee-code" save-state>
        <p>
          <i18n path="employee.enroll.codeBody1" tag="span">
            <template #employeeCodeBold
              ><strong>{{ $t('employee.enroll.employeeCodeBold') }}</strong></template
            >
          </i18n>
        </p>
        <p>
          <i18n path="employee.enroll.codeBody2" tag="span">
            <template #shiftsLink
              ><router-link :to="{ name: 'shifts' }">{{ $t('employee.enroll.shiftsLink') }}</router-link></template
            >
          </i18n>
        </p>
        <p>
          <i18n path="employee.enroll.codeBody3" tag="span">
            <template #integrationLink
              ><router-link :to="{ name: 'sme-integrations' }">{{
                $t('employee.add.integrationsLink')
              }}</router-link></template
            >
          </i18n>
        </p>
      </sme-info-box>
    </template>

    <template v-if="loading">
      <app-loading />
    </template>
    <template v-else>
      <p v-if="!canAddSingleEmployee || !canAddEmployees">
        {{ $t('employee.add.noPermissionBody') }}
      </p>

      <employee-add-form
        v-if="canAddEmployees && canAddSingleEmployee"
        v-model="employee"
        :balance="balance"
        :field-permissions="fieldPermissions"
        :save-error="saveError"
        :saving="saving"
        :state="state"
        :integrations-state="integrationsState"
        :hide-earnings="hideEarnings"
        :collapse-advanced-settings="false"
        @save="onSave"
      >
      </employee-add-form>
    </template>
  </app-page>
</template>

<script>
import uuidv4 from 'uuid/v4';
import ApiClient from '@/ApiClient';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import SmeInfoBox from '@/components/atoms/SmeInfoBox.vue';
import { employeeHandle } from '@/mixins/EmployeeHandle.js';
import EmployeeAddForm from '@/pages/employeeAdd/components/EmployeeAddForm.vue';
import IntegrationsState from '@/state/IntegrationsState';
import State from '@/state/State';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useAnalytics from '@/state/composables/useAnalytics';

export default {
  name: 'EmployeeAdd',
  components: {
    AppLoading,
    AppPage,
    EmployeeAddForm,
    SmeInfoBox,
  },
  mixins: [employeeHandle],
  data() {
    return {
      employee: undefined,
      balance: undefined,
      fieldPermissions: undefined,
      error: undefined,
      loading: true,
      saveError: undefined,
      saving: false,
      state: State.state,
      integrationsState: IntegrationsState.state,
    };
  },
  setup() {
    const { canAddEmployees, canAddSingleEmployee } = useFeatureFlags();
    const { postAnalyticsEvent } = useAnalytics();

    return {
      canAddEmployees,
      canAddSingleEmployee,
      postAnalyticsEvent,
    };
  },
  computed: {
    hideEarnings() {
      return this.state.company.properties.hide_new_earnings && this.employee.current_state === 'NEW';
    },
  },
  mounted() {
    this.postAnalyticsEvent({
      intent: 'view_add_single_employee',
      task: 'add_employees',
      action: 'view',
    });
    this.employee = this.setEmployeeDefaults({});
    ApiClient.getCompanyFieldPermissions(this.state.company.company_id).then(
      fieldPermissions => {
        this.fieldPermissions = fieldPermissions;
        this.loading = false;
      },
      error => {
        this.error = error.message;
        this.loading = false;
      },
    );
  },
  methods: {
    onSave() {
      this.saveError = undefined;
      this.saving = true;
      this.ensureNoEmptyStringsInEmployee();
      const newEmployee = {
        ...this.employee,
        employee_id: uuidv4(),
        pay_schedule_id: this.state.company.default_pay_schedule_id,
        company_id: this.state.company.company_id,
      };
      this.postAnalyticsEvent({
        intent: 'complete_add_single_employee',
        task: 'add_employees',
        action: 'click',
      });
      ApiClient.postEmployee(newEmployee).then(
        data => {
          this.saving = false;
          let message = this.$t('employee.add.successToastBody', { name: this.employee.full_name });
          let noAutoHide = false;
          if (data.password) {
            message += this.$t('employee.add.successToastPasswordBody', { password: data.password });
            noAutoHide = true;
          }
          this.$appToast(message, {
            title: this.$t('employee.add.successToastTitle'),
            variant: 'success',
            noAutoHide: noAutoHide,
          });
          this.$router.push({ name: 'employee', params: { employeeId: newEmployee.employee_id } });
        },
        error => {
          if (error.message.includes('uniq__employees__company_id_employee_code')) {
            this.saveError = this.$t('employee.add.duplicateCodeError', { code: this.employee.employee_code });
          } else if (error.message.includes('uniq__employees__company_id_work_phone')) {
            this.saveError = this.$t('employee.add.duplicatePhoneError', { phone: this.employee.work_phone });
          } else if (error.message.includes('uniq__employees__company_id_work_email')) {
            this.saveError = this.$t('employee.add.duplicateEmailError', { email: this.employee.work_email });
          } else {
            this.saveError = error.message;
          }
          this.saving = false;
        },
      );
    },
  },
};
</script>
